/* eslint-disable react-hooks/exhaustive-deps */
/**
 * Phase 16 — Personal To-Do & Follow-ups
 * Per-user only. No cross-user sharing.
 * Includes: voice note (60s), up to 3 attachments (5MB each),
 *           manual WhatsApp share, reschedule, status toggle.
 */
import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  CheckCircle2, Trash2, Plus, Loader2, Share2, ListTodo,
  Paperclip, X, FileText,
} from "lucide-react";
import VoiceRecorder from "@/components/VoiceRecorder";

const today = () => new Date().toISOString().slice(0, 10);

function badgeFor(t) {
  if (t.status === "done") return { label: "Done", cls: "bg-slate-100 text-slate-500 line-through" };
  if (!t.follow_up_date) return { label: "No follow-up", cls: "bg-slate-100 text-slate-500" };
  const td = today();
  if (t.follow_up_date < td) return { label: `${t.follow_up_date} · Overdue`, cls: "bg-red-50 text-red-700 ring-1 ring-red-200" };
  if (t.follow_up_date === td) return { label: `${t.follow_up_date} · Due today`, cls: "bg-amber-50 text-amber-700 ring-1 ring-amber-200" };
  return { label: `${t.follow_up_date} · Upcoming`, cls: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200" };
}

function shareWA(t) {
  const lines = [
    `Reminder: ${t.task}`,
    t.follow_up_date ? `Follow up: ${t.follow_up_date}` : null,
    t.responsible ? `Owner: ${t.responsible}` : null,
  ].filter(Boolean);
  const msg = lines.join("\n");
  // Always open WhatsApp directly so users see WhatsApp option even on
  // desktops where navigator.share opens the native OS share sheet (which
  // does NOT include WhatsApp on macOS/Windows).
  const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
  const url = isMobile
    ? `https://wa.me/?text=${encodeURIComponent(msg)}`
    : `https://web.whatsapp.com/send?text=${encodeURIComponent(msg)}`;
  // Open in new tab on desktop; same-tab redirect on mobile (matches WA app handoff)
  if (isMobile) window.location.href = url;
  else window.open(url, "_blank", "noopener,noreferrer");
}

const MAX_ATTACH = 3;
const MAX_SIZE = 5 * 1024 * 1024;
const ATTACH_TYPES = /^(image\/|application\/pdf$)/i;

export default function PortalTodos() {
  const [list, setList] = useState([]);
  const [counters, setCounters] = useState({ open: 0, due_today: 0, overdue: 0 });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    task: "", task_date: today(), follow_up_date: "", responsible: "",
    voice_url: null, attachments: [],
  });
  const [busy, setBusy] = useState(false);
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [rescheduleId, setRescheduleId] = useState(null);
  const fileInputRef = useRef(null);

  const resetForm = () => setForm({
    task: "", task_date: today(), follow_up_date: "", responsible: "",
    voice_url: null, attachments: [],
  });

  const load = async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([
        api.get("/my-portal/todos"),
        api.get("/my-portal/todos/counters"),
      ]);
      setList(r.data || []);
      setCounters(c.data || {});
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Load failed");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!form.task.trim()) { toast.error("Task is required"); return; }
    setBusy(true);
    try {
      await api.post("/my-portal/todos", {
        task: form.task.trim(),
        task_date: form.task_date || null,
        follow_up_date: form.follow_up_date || null,
        responsible: form.responsible.trim() || null,
        voice_url: form.voice_url || null,
        attachments: form.attachments,
      });
      toast.success("Task added");
      resetForm();
      setShowForm(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    } finally { setBusy(false); }
  };

  const toggle = async (t) => {
    try {
      await api.patch(`/my-portal/todos/${t.id}/status`, {
        status: t.status === "done" ? "open" : "done",
      });
      await load();
    } catch (e) { toast.error("Failed to update"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try {
      await api.delete(`/my-portal/todos/${id}`);
      toast.success("Deleted");
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const reschedule = async (id, newDate) => {
    try {
      await api.patch(`/my-portal/todos/${id}`, { follow_up_date: newDate || null });
      toast.success("Rescheduled");
      setRescheduleId(null);
      await load();
    } catch (e) { toast.error("Failed"); }
  };

  const onPickFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ""; // reset so same file can be re-picked
    if (!files.length) return;
    if (form.attachments.length + files.length > MAX_ATTACH) {
      toast.error(`Max ${MAX_ATTACH} attachments`);
      return;
    }
    setUploadingAttach(true);
    const next = [...form.attachments];
    for (const f of files) {
      if (f.size > MAX_SIZE) { toast.error(`${f.name}: exceeds 5MB`); continue; }
      if (!ATTACH_TYPES.test(f.type)) { toast.error(`${f.name}: only images/PDF allowed`); continue; }
      try {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("purpose", "todo_attachment");
        const { data } = await api.post("/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        next.push({
          url: `/api/files/${data.path}`,
          name: f.name,
          content_type: f.type,
          size: f.size,
        });
      } catch (err) {
        toast.error(`Upload failed: ${f.name}`);
      }
    }
    setForm((p) => ({ ...p, attachments: next }));
    setUploadingAttach(false);
  };

  const removeAttachment = (idx) => {
    setForm((p) => ({ ...p, attachments: p.attachments.filter((_, i) => i !== idx) }));
  };

  return (
    <div className="space-y-5 max-w-5xl mx-auto" data-testid="portal-todos">
      {/* Header + counters */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-red-700 font-semibold">Employee Portal</div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: "Chivo" }}>
            To-Do &amp; Follow-ups
          </h1>
          <p className="text-xs text-slate-500">
            Personal tasks only. Voice notes, attachments &amp; reminders on follow-up date.
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {[
            { k: "open", label: "Open", tone: "bg-white ring-slate-200 text-slate-900" },
            { k: "due_today", label: "Due today", tone: "bg-amber-50 ring-amber-200 text-amber-800" },
            { k: "overdue", label: "Overdue", tone: "bg-red-50 ring-red-200 text-red-700" },
          ].map((c) => (
            <div key={c.k} data-testid={`todo-counter-${c.k}`}
                 className={`rounded-xl ring-1 ${c.tone} px-4 py-2 text-center min-w-[78px]`}>
              <div className="text-2xl font-bold leading-none">{counters[c.k] ?? 0}</div>
              <div className="text-[10px] uppercase tracking-widest opacity-80 mt-1">{c.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Toggle add form */}
      {!showForm && (
        <button onClick={() => setShowForm(true)} data-testid="todo-show-form-btn"
                className="w-full sm:w-auto rounded-xl bg-red-600 hover:bg-red-700 text-white px-4 py-3 text-sm font-bold flex items-center justify-center gap-2">
          <Plus className="h-4 w-4" /> Add new task
        </button>
      )}

      {/* Add form */}
      {showForm && (
        <div className="bg-white rounded-xl ring-1 ring-slate-200 p-4 space-y-3" data-testid="todo-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Task *</label>
              <input value={form.task} onChange={(e) => setForm({ ...form, task: e.target.value })}
                     placeholder="What needs doing?"
                     data-testid="todo-input-task"
                     className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Date</label>
              <input type="date" value={form.task_date}
                     onChange={(e) => setForm({ ...form, task_date: e.target.value })}
                     data-testid="todo-input-date"
                     className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Follow-up date</label>
              <input type="date" value={form.follow_up_date}
                     onChange={(e) => setForm({ ...form, follow_up_date: e.target.value })}
                     data-testid="todo-input-followup"
                     className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm" />
            </div>
            <div className="sm:col-span-2">
              <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Responsible (free text)</label>
              <input value={form.responsible} onChange={(e) => setForm({ ...form, responsible: e.target.value })}
                     placeholder="Self / Vendor / Bank…"
                     data-testid="todo-input-responsible"
                     className="w-full mt-1 rounded-lg ring-1 ring-slate-300 px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Voice note */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Voice note (optional, 60s)</label>
            <div className="mt-1">
              <VoiceRecorder
                value={form.voice_url}
                onChange={(url) => setForm((p) => ({ ...p, voice_url: url }))}
                uploadFields={{ purpose: "todo_voice" }}
                testIdPrefix="todo-voice"
                maxSeconds={60}
              />
            </div>
          </div>

          {/* Attachments */}
          <div>
            <label className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">
              Attachments (max {MAX_ATTACH}, 5MB each, images/PDF)
            </label>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <button type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploadingAttach || form.attachments.length >= MAX_ATTACH}
                      data-testid="todo-pick-files-btn"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-red-200 rounded bg-white hover:bg-red-50 brand-text disabled:opacity-50">
                {uploadingAttach ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Paperclip className="h-3.5 w-3.5" />}
                {uploadingAttach ? "Uploading…" : "Attach file"}
              </button>
              <input ref={fileInputRef} type="file" multiple accept="image/*,application/pdf"
                     onChange={onPickFiles} className="hidden"
                     data-testid="todo-files-input" />
              {form.attachments.map((a, i) => (
                <span key={i} data-testid={`todo-attach-chip-${i}`}
                      className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 text-[11px] rounded-full px-2 py-1">
                  <FileText className="h-3 w-3" />
                  <a href={a.url} target="_blank" rel="noreferrer" className="hover:underline truncate max-w-[140px]">
                    {a.name || "file"}
                  </a>
                  <button onClick={() => removeAttachment(i)} className="text-slate-500 hover:text-red-600">
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={() => { resetForm(); setShowForm(false); }}
                    className="rounded-lg ring-1 ring-slate-300 px-4 py-2 text-sm font-semibold text-slate-700">
              Cancel
            </button>
            <button onClick={add} disabled={busy || !form.task.trim()}
                    data-testid="todo-add-btn"
                    className="rounded-lg bg-red-600 hover:bg-red-700 text-white px-4 py-2 text-sm font-bold flex items-center gap-1.5 disabled:bg-slate-300">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
              Save task
            </button>
          </div>
        </div>
      )}

      {/* Task list */}
      <div className="bg-white rounded-xl ring-1 ring-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-xs text-slate-400">Loading…</div>
        ) : list.length === 0 ? (
          <div className="p-8 text-center text-xs text-slate-400">
            <ListTodo className="h-8 w-8 mx-auto mb-2 opacity-30" />
            No tasks yet. Add your first above.
          </div>
        ) : (
          list.map((t) => {
            const b = badgeFor(t);
            const isDone = t.status === "done";
            return (
              <div key={t.id} data-testid={`todo-row-${t.id}`}
                   className={`px-4 py-3 border-t border-slate-100 first:border-t-0 ${isDone ? "opacity-60" : ""}`}>
                <div className="flex items-start gap-3">
                  <button onClick={() => toggle(t)}
                          data-testid={`todo-toggle-${t.id}`}
                          className="mt-0.5 flex-shrink-0">
                    {isDone ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                    ) : (
                      <div className="h-5 w-5 rounded-full ring-2 ring-slate-300 hover:ring-red-400" />
                    )}
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className={`text-sm font-medium ${isDone ? "line-through text-slate-500" : "text-slate-900"}`}>
                      {t.task}
                    </div>
                    <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1.5 text-[11px] text-slate-500">
                      <span>📅 {t.task_date}</span>
                      {t.responsible && <span>👤 {t.responsible}</span>}
                      <div className="relative">
                        <button onClick={() => setRescheduleId(rescheduleId === t.id ? null : t.id)}
                                data-testid={`todo-reschedule-${t.id}`}
                                className={`text-[11px] rounded-md px-2 py-0.5 font-semibold ${b.cls}`}>
                          {b.label}
                        </button>
                        {rescheduleId === t.id && (
                          <div className="absolute z-10 mt-1 bg-white rounded-lg ring-1 ring-slate-300 shadow-lg p-2">
                            <input type="date" defaultValue={t.follow_up_date || ""}
                                   onChange={(e) => reschedule(t.id, e.target.value)}
                                   className="text-xs rounded ring-1 ring-slate-300 px-2 py-1" />
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Voice + attachments */}
                    {(t.voice_url || (t.attachments && t.attachments.length > 0)) && (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        {t.voice_url && (
                          <audio controls src={t.voice_url} className="h-8 max-w-[220px]" data-testid={`todo-voice-${t.id}`} />
                        )}
                        {(t.attachments || []).map((a, i) => (
                          <a key={i} href={a.url} target="_blank" rel="noreferrer"
                             data-testid={`todo-attach-${t.id}-${i}`}
                             className="inline-flex items-center gap-1 bg-slate-100 text-slate-700 hover:bg-slate-200 text-[11px] rounded-full px-2 py-1">
                            <FileText className="h-3 w-3" />
                            <span className="truncate max-w-[140px]">{a.name || "file"}</span>
                          </a>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-1 flex-shrink-0">
                    {!isDone && (
                      <button onClick={() => shareWA(t)}
                              data-testid={`todo-wa-${t.id}`}
                              title="Share on WhatsApp"
                              className="text-emerald-600 hover:text-emerald-700 p-1">
                        <Share2 className="h-4 w-4" />
                      </button>
                    )}
                    <button onClick={() => remove(t.id)}
                            data-testid={`todo-delete-${t.id}`}
                            className="text-slate-400 hover:text-red-600 p-1">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      <div className="text-[11px] text-slate-500 bg-red-50 rounded-lg p-3 ring-1 ring-red-100">
        💡 Daily 8 AM IST: a reminder appears in your notification bell for every
        follow-up due today. Tap the share icon to forward a task via WhatsApp
        manually (you choose the contact).
      </div>
    </div>
  );
}
